import React, { useMemo } from 'react';
import { mkNode, mkEdge, FlowCanvas } from './flowParts';

// Module + capability map: how the web app, platform services, backend workers,
// user-facing capabilities and storage connect.
const C = {
  app: '#14e0c8',
  fb: '#e0a414',
  api: '#5b8def',
  worker: '#e0517a',
  feature: '#9b6cf2',
  storage: '#3fbf6f',
};

const NODES = [
  mkNode('spa', 0, 360, 'Web App', 'React 18 SPA · Router', C.app),

  // Platform services.
  mkNode('auth', 300, 60, 'Firebase Auth', 'Email/Password', C.fb),
  mkNode('firestore', 300, 200, 'Cloud Firestore', 'realtime + job queue', C.fb),
  mkNode('uploadapi', 300, 360, 'Upload / API', 'presigned S3 · frame grab', C.api),
  mkNode('cdn', 300, 520, 'CloudFront CDN', 'HLS · media · HTTP/3', C.api),

  // Backend workers (Fargate ffmpeg / LibRaw / ONNX).
  mkNode('render', 660, 0, 'Render · Publish', 'ffmpeg HLS + grade bake', C.worker, 190),
  mkNode('master', 660, 90, 'Master compose', 'flat 1080p MP4', C.worker, 190),
  mkNode('reframe', 660, 180, 'Reframe · YOLOv8n', 'subject tracking', C.worker, 190),
  mkNode('social', 660, 270, 'Social export', 'subject-centered crops', C.worker, 190),
  mkNode('develop', 660, 360, 'RAW develop', 'LibRaw · 16-bit proxy', C.worker, 190),
  mkNode('waveform', 660, 450, 'Waveform', 'ffmpeg peaks · yt-dlp', C.worker, 190),
  mkNode('thumbs', 660, 540, 'Thumbnails', 'poster frames', C.worker, 190),
  mkNode('highlight', 660, 630, 'Trip highlights', 'hover-preview clips', C.worker, 190),
  mkNode('meta', 660, 720, 'Metadata', 'ffprobe · fps · lineage', C.worker, 190),
  mkNode('conditions', 660, 810, 'Surf conditions', 'swell · wind · tide', C.worker, 190),
  mkNode('fanout', 660, 900, 'Fan-out orchestrator', 'RED decode · ephemeral tasks', C.worker, 190),
  mkNode('r3d', 660, 990, 'RED R3D proxy', 'SDK decode → HLS', C.worker, 190),

  // Capabilities / outcomes.
  mkNode('streaming', 1000, 0, 'HLS streaming', 'buffer gauge · quality', C.feature, 190),
  mkNode('color', 1000, 95, 'Color grading', 'WebGL LUT == ffmpeg', C.feature, 190),
  mkNode('socialout', 1000, 190, 'Social formats', 'IG · TikTok · X', C.feature, 190),
  mkNode('rawedit', 1000, 285, 'RAW photo editor', 'non-destructive', C.feature, 190),
  mkNode('stills', 1000, 380, 'Full-res stills', 'frame grab export', C.feature, 190),
  mkNode('music', 1000, 475, 'Music sync', 'YouTube/cloud overlay', C.feature, 190),
  mkNode('waveui', 1000, 570, 'Waveform editor', 'zoomable timeline', C.feature, 190),
  mkNode('globe', 1000, 665, 'Trips globe', 'd3-geo · markers', C.feature, 190),
  mkNode('condui', 1000, 760, 'Surf conditions', 'per clip + per day', C.feature, 190),

  // Storage + external data.
  mkNode('s3raw', 1320, 300, 'S3 · raw', 'originals', C.storage, 160),
  mkNode('s3movies', 1320, 440, 'S3 · movies + CDN', 'derived assets', C.storage, 160),
  mkNode('efs', 1320, 580, 'EFS scratch', 'shared decode parts', C.storage, 160),
  mkNode('weather', 1320, 800, 'Open-Meteo + Stormglass', 'marine · wind · tide', C.api, 200),
];

const q = { animated: true, color: 'rgba(224,164,20,0.55)' }; // firestore job-queue edges
const W = (c = C.worker) => ({ color: c });

const EDGES = [
  // App → platform.
  mkEdge('spa', 'auth', '', { color: C.fb }),
  mkEdge('spa', 'firestore', 'onSnapshot', { animated: true, color: C.fb }),
  mkEdge('spa', 'uploadapi', '', { color: C.api }),
  mkEdge('spa', 'cdn', 'playback', { animated: true, color: C.api }),

  // App → client-side capabilities.
  mkEdge('spa', 'streaming', '', { color: C.feature }),
  mkEdge('spa', 'color', 'edit', { color: C.feature }),
  mkEdge('spa', 'music', '', { color: C.feature }),
  mkEdge('spa', 'waveui', '', { color: C.feature }),
  mkEdge('spa', 'globe', '', { color: C.feature }),
  mkEdge('spa', 'rawedit', 'edit', { color: C.feature }),

  // Firestore status flags drive the workers (queue).
  mkEdge('firestore', 'render', '', q),
  mkEdge('firestore', 'master', '', q),
  mkEdge('firestore', 'reframe', '', q),
  mkEdge('firestore', 'social', '', q),
  mkEdge('firestore', 'develop', '', q),
  mkEdge('firestore', 'waveform', '', q),
  mkEdge('firestore', 'thumbs', '', q),
  mkEdge('firestore', 'highlight', '', q),
  mkEdge('firestore', 'meta', '', q),
  mkEdge('firestore', 'conditions', '', q),
  mkEdge('firestore', 'r3d', '', q),

  // Workers → outcomes / storage.
  mkEdge('render', 'streaming', '', W()),
  mkEdge('render', 's3movies', '', W(C.storage)),
  mkEdge('color', 'render', 'grade', { color: C.feature }),
  mkEdge('color', 'develop', 'grade', { color: C.feature }),
  mkEdge('master', 'reframe', '', W()),
  mkEdge('master', 'social', '', W()),
  mkEdge('reframe', 'social', 'waypoints', W()),
  mkEdge('social', 'socialout', '', W()),
  mkEdge('social', 's3movies', '', W(C.storage)),
  mkEdge('develop', 'rawedit', '', W()),
  mkEdge('develop', 's3raw', 'read', W(C.storage)),
  mkEdge('develop', 's3movies', '', W(C.storage)),
  mkEdge('waveform', 'waveui', '', W()),
  mkEdge('thumbs', 's3movies', '', W(C.storage)),
  mkEdge('highlight', 's3movies', '', W(C.storage)),
  mkEdge('meta', 's3raw', 'probe', W(C.storage)),
  mkEdge('conditions', 'weather', 'fetch', W(C.api)),
  mkEdge('conditions', 'condui', '', W()),

  // RED decode fan-out: the proxy worker delegates to the orchestrator, which
  // launches ephemeral runner tasks that decode to EFS and stitch to S3.
  mkEdge('r3d', 'fanout', 'delegate', W()),
  mkEdge('fanout', 'efs', 'decode parts', W(C.storage)),
  mkEdge('fanout', 's3movies', 'stitch', W(C.storage)),
  mkEdge('fanout', 'streaming', '', W()),
  mkEdge('render', 'fanout', 'RAW pre-pass', W()),

  // API → stills + storage.
  mkEdge('uploadapi', 'stills', '', { color: C.api }),
  mkEdge('uploadapi', 's3raw', 'multipart', { color: C.storage }),
  mkEdge('cdn', 's3movies', 'origin', { color: C.api }),
];

const LEGEND = [
  ['Web app', C.app],
  ['Firebase', C.fb],
  ['API / CDN', C.api],
  ['Backend workers', C.worker],
  ['Capabilities', C.feature],
  ['Storage', C.storage],
];

export default function ModulesMap() {
  const nodes = useMemo(() => NODES, []);
  const edges = useMemo(() => EDGES, []);
  return <FlowCanvas nodes={nodes} edges={edges} legend={LEGEND} height={560} />;
}
